import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadStoreSettings } from "../../util/storeSettings";
import {
  FaQuestionCircle,
  FaShieldAlt,
  FaTruck,
  FaHeadset,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";
import SEOHead from "../common/SEOHead";
import { applyStoreTitle } from "../../util/storeLogoHelper";

export default function About() {
  const [openIndex, setOpenIndex] = useState(0);
  const [storeSettings, setStoreSettings] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadStoreSettings().then((settings) => {
      if (settings && (settings.storeName || settings.logoUrl)) {
        setStoreSettings(settings);
        applyStoreTitle(settings, "About Us & FAQs");
      } else {
        applyStoreTitle(null, "About Us & FAQs");
      }
    }).catch(() => {
      applyStoreTitle(null, "About Us & FAQs");
    });
  }, []);

  const storeName = storeSettings?.storeName || "Gootocart";
  const country = storeSettings?.country || "Pakistan";

  const faqs = [
    {
      q: `What is ${storeName}?`,
      a: `${storeName} is a premier online shopping destination offering authentic electronics, lifestyle apparel, mobile devices, and home appliances at resounding discounts.`,
    },
    {
      q: "How can I pay for my order?",
      a: "Pay with cash on delivery, or transfer the order total to the store bank account. The order stays pending until a bank transfer is confirmed.",
    },
    {
      q: `Can you ship orders all over ${country}?`,
      a: `Yes! We provide swift nationwide delivery across all major cities and towns.`,
    },
    {
      q: "How can I track the status of my order?",
      a: "Once your order is confirmed, our logistics team provides tracking details via SMS and email. You can also view recent order statuses in your account dashboard.",
    },
    {
      q: "What is your return & refund policy?",
      a: "We provide a 7-day hassle-free return policy. If you receive a damaged or incorrect product, contact our support team within 7 days of delivery for free replacement or refund.",
    },
    {
      q: "Do you verify orders before dispatch?",
      a: "Yes, for Cash on Delivery orders, our customer care team may call or SMS your registered phone number to confirm the order details before swift dispatch.",
    },
  ];

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div>
      <SEOHead
        title="About Us & FAQs"
        description={`Learn more about ${storeName}, our commitment to authentic products, nationwide fast delivery, and customer satisfaction.`}
        url="/about"
        type="website"
      />

      {/* Header Banner & Breadcrumbs */}
      <div className="page-header-modern">
        <div className="container">
          <ul className="breadcrumb-modern">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">About Us</li>
          </ul>
          <h1 className="page-header-title">About {storeName}</h1>
        </div>
      </div>

      <div className="container py-4">
        {/* Story Section */}
        <div className="row align-items-center g-5 mb-5">
          <div className="col-12 col-lg-6">
            <span
              className="badge px-3 py-2 mb-3"
              style={{ background: "var(--accent-light)", color: "var(--accent)", fontWeight: 700 }}
            >
              Our Mission
            </span>
            <h2 className="fw-bold mb-3" style={{ fontSize: "clamp(24px, 3.5vw, 34px)" }}>
              Empowering Modern Shoppers with Quality & Trust
            </h2>
            <p style={{ lineHeight: 1.8 }}>
              {storeSettings?.footerDescription ||
                `Founded with the goal of revolutionizing e-commerce in ${country}, ${storeName} delivers an uncompromising standard of quality, curated collections, and customer-first service.`}
            </p>
            <div className="row g-3 mt-2">
              <div className="col-6">
                <div className="p-3 bg-white rounded-3 border">
                  <h3 className="fw-bold text-primary mb-1">800+</h3>
                  <span className="text-muted" style={{ fontSize: "13px" }}>Cities Covered</span>
                </div>
              </div>
              <div className="col-6">
                <div className="p-3 bg-white rounded-3 border">
                  <h3 className="fw-bold text-success mb-1">100%</h3>
                  <span className="text-muted" style={{ fontSize: "13px" }}>Authentic Guarantee</span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div
              className="p-4 p-md-5 rounded-4"
              style={{
                background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                color: "#fff",
                boxShadow: "var(--shadow-lg)"
              }}
            >
              <h3 className="fw-bold text-white mb-4">Why Choose {storeName}?</h3>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-start gap-3">
                  <div className="p-2 rounded-circle bg-white text-dark mt-1">
                    <FaTruck size={14} />
                  </div>
                  <div>
                    <h6 className="fw-bold text-white mb-1">Swift Nationwide Shipping</h6>
                    <p className="m-0 text-light opacity-75" style={{ fontSize: "13.5px" }}>
                      Express dispatch with tracked delivery partners.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-start gap-3">
                  <div className="p-2 rounded-circle bg-white text-dark mt-1">
                    <FaShieldAlt size={14} />
                  </div>
                  <div>
                    <h6 className="fw-bold text-white mb-1">256-Bit Encrypted Payments</h6>
                    <p className="m-0 text-light opacity-75" style={{ fontSize: "13.5px" }}>
                      Pay with cash on delivery or a bank transfer.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-start gap-3">
                  <div className="p-2 rounded-circle bg-white text-dark mt-1">
                    <FaHeadset size={14} />
                  </div>
                  <div>
                    <h6 className="fw-bold text-white mb-1">Dedicated Customer Support</h6>
                    <p className="m-0 text-light opacity-75" style={{ fontSize: "13.5px" }}>
                      Support representatives ready to assist you 7 days a week.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQs Section */}
        <div className="my-5">
          <div className="text-center max-w-xl mx-auto mb-4">
            <span
              className="badge px-3 py-2 mb-2"
              style={{ background: "var(--accent-gold-light)", color: "var(--accent-gold)", fontWeight: 700 }}
            >
              <FaQuestionCircle className="me-1" /> Help & Support
            </span>
            <h2 className="fw-bold">Frequently Asked Questions</h2>
            <p className="text-muted">Find fast answers to common questions regarding orders, payments, and shipping.</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-12 col-lg-9">
              <div className="d-flex flex-column gap-3">
                {faqs.map((faq, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div
                      key={index}
                      className="border rounded-3 bg-white overflow-hidden transition"
                      style={{
                        boxShadow: isOpen ? "var(--shadow-md)" : "var(--shadow-xs)",
                        borderColor: isOpen ? "var(--accent)" : "var(--border-color)",
                      }}
                    >
                      <button
                        onClick={() => toggleFaq(index)}
                        className="btn w-100 p-4 text-start d-flex justify-content-between align-items-center"
                        style={{ background: isOpen ? "var(--accent-light)" : "transparent" }}
                      >
                        <span className="fw-bold" style={{ fontSize: "15px", color: "var(--text-main)" }}>
                          {faq.q}
                        </span>
                        {isOpen ? (
                          <FaChevronUp color="var(--accent)" />
                        ) : (
                          <FaChevronDown color="var(--text-muted)" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="p-4 pt-2 border-top" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
